import React, { useEffect } from 'react';
import { omit, pick } from 'ramda';
import { useDispatch } from 'react-redux';

import { log } from 'log';
import { useNavigate } from 'js/hooks/navigate';
import { useTranslations } from 'js/hooks/i18n';
import { useUserSelector } from 'js/store/hooks';
import { setPlayerTags } from '@core/js/hooks/user/server';
import { useDynamicTags } from 'js/app/hooks/useDynamicTags';
import { useAfterLoginAction } from '@interlayer/js/hooks/auth/hooks/useAfterLoginAction';
import { useGtmEvent } from 'js/app/hooks/analiticsSubscribe';
import { DYNAMIC_TAG_NAMES } from '@interlayer/js/app/utils/constants';
import { useNavigateToHome } from 'js/app/hooks/navigate/useNavigateToHome';
import { setAllPlayerTags, setJumioStep } from 'js/hooks/user/store/buzz/actions';
import { PLAYER_TAG_IDV_CHECKED, PLAYER_TAG_IDV_UPLOADED } from '@interlayer/js/components/system/Page/buzz/constants';

import {
    JumioStepStart,
    JumioStepLoading,
    JumioStepIframe,
    JumioStepResult,
    JumioStepPassed
} from './components';

import { ADDITIONAL_UB_STEP_REQUIRED_FOR, DOCUMENT_TYPE_MAP, JUMIO_STEPS } from './constants';

export const JumioPopupUploader = ({ setVerificationPassed, onClose }) => {
    const gtmEvent = useGtmEvent();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [
        dTagJumioVerificationTimeout = 40000,
        dTagJumioAfterPassedPageUrl
    ] = useDynamicTags([
        DYNAMIC_TAG_NAMES.jumioVerificationTimeout,
        DYNAMIC_TAG_NAMES.jumioAfterPassedPageUrl
    ]);

    const afterLoginAction = useAfterLoginAction();
    const translate = useTranslations('playtech.mobile.system.id-verification.');
    const {
        jumioState,
        jumioStep,
        allPlayerTags
    } = useUserSelector(pick(['jumioState', 'jumioStep', 'allPlayerTags']));

    const navigateToHome = useNavigateToHome();
    const setCurrentStep = step => dispatch(setJumioStep(step));

    const handlerAfterPassed = () => {
        onClose();

        if (dTagJumioAfterPassedPageUrl) {
            navigate(dTagJumioAfterPassedPageUrl);
        }

        afterLoginAction();
    };

    const handlerTakeMeHome = () => {
        onClose({ isOnlyLogout: true });

        navigateToHome();
    };

    const getSubView = (step) => {
        const subViewMap = {
            [JUMIO_STEPS.start]: <JumioStepStart translate={translate} setCurrentStep={setCurrentStep} />,
            [JUMIO_STEPS.loading]: (
                <JumioStepLoading
                    translate={translate}
                    setCurrentStep={setCurrentStep}
                    handlerAfterPassed={handlerAfterPassed}
                    setVerificationPassed={setVerificationPassed}
                    verificationTimeout={dTagJumioVerificationTimeout}
                />
            ),
            [JUMIO_STEPS.takeMeHome]: (
                <JumioStepResult
                    setCurrentStep={setCurrentStep}
                    tKey='upload-success'
                    translate={translate}
                    handlerTakeMeHome={handlerTakeMeHome}
                />
            ),
            [JUMIO_STEPS.error]: <JumioStepResult setCurrentStep={setCurrentStep} translate={translate} tKey='upload-error' showTryAgain />,
            [JUMIO_STEPS.passed]: <JumioStepPassed translate={translate} handlerAfterPassed={handlerAfterPassed} />
        };

        if (step === JUMIO_STEPS.passed) {
            setVerificationPassed(true);
        }

        if (subViewMap[step]) {
            return subViewMap[step];
        }

        return (
            <JumioStepIframe
                key={step}
                translate={translate}
                documentType={DOCUMENT_TYPE_MAP[step]}
                setCurrentStep={setCurrentStep}
                isHideBackButton={jumioState === 'gracePeriod'}
            />
        );
    };

    const removePlayerTagIdvChecked = () => {
        dispatch(setAllPlayerTags(omit([PLAYER_TAG_IDV_CHECKED], allPlayerTags)));
    };

    useEffect(() => {
        if (jumioStep === 'passed' || jumioStep === 'start') {
            gtmEvent(`IDV ${jumioStep}`);
        }
    }, [jumioStep, gtmEvent]);

    useEffect(() => {
        const handleMessage = message => {
            let data = {};

            try {
                data = JSON.parse(message?.data);
            } catch (e) {
                log('error', e);
                return;
            }

            if (data?.payload?.value === 'success') {
                if (ADDITIONAL_UB_STEP_REQUIRED_FOR.indexOf(jumioStep) > -1) {
                    setCurrentStep(JUMIO_STEPS.utilityBill);
                } else if (jumioState !== 'gracePeriod') {
                    gtmEvent('jumio_upload-complete');
                    removePlayerTagIdvChecked();
                    setPlayerTags([{ name: PLAYER_TAG_IDV_UPLOADED }]);

                    setCurrentStep(JUMIO_STEPS.loading);
                }
            } else if (data?.payload?.value === 'error') {
                gtmEvent('jumio_upload-error');
                setCurrentStep(JUMIO_STEPS.error);
            }
        };

        window.addEventListener('message', handleMessage, false);

        return () => {
            window.removeEventListener('message', handleMessage);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jumioStep]);

    return getSubView(jumioStep);
};
